import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpHeaders, HttpParams } from '@angular/common/http';
import { BehaviorSubject, Observable, of, throwError } from 'rxjs';
import { catchError, distinctUntilChanged, first, map, shareReplay, switchMap, take, tap, withLatestFrom } from 'rxjs/operators';
import {
  DataParametersResponse,
  Ambiguity,
  FindKbMatchesResponse,
  AgentKbResolveResponse,
  AgentKbResolveSingleResponse,
  ProcessAmbiguitiesResponse,
  TokenCount,
  RefinementRequest,
  RefinementResponse, GroupingRequest, GroupingResponse, AnalysisContent, RawTestCase
} from './templateInterfaces';
import { apiUrl } from '../../../environments/environment';
import { AuthService } from '../auth/auth.service';
import { InferenceParams, StatoSessioneService } from '../services/stato-sessione.service';
import { KnowledgeBaseItem } from '../services/persistence-api.service';
import {
  MacroScenarioItem, MacroScenarioListResult,
  MicroScenarioSpecificationListResultModel,
  MicroScenarioSpecificationModel
} from '../../modules/projects/template/three-column-workspace/three-column-workspace.component';

/**
 * Defines the request body for the /cover_hanging_requirements/ endpoint.
 */
export interface ChatResponse {
  answer: string;
  token_usage: TokenCount;
}

export interface AgentChatSource {
  file_name: string;
  snippet: string;
  title?: string;
  evidence_quote?: string;  // Exact quote from document for highlighting
}

export interface AgentChatResponse {
  answer: string;
  tool_calls_log: any[];
  sources: AgentChatSource[];
  debug: any;
  token_usage: TokenCount;
}

export interface HangingRequirementCoverageRequest {
  nome_sessione: string,
  project: string,
  uncovered_requirements_text: string;
  all_requirements_text: string;
  story: string;
  inference_params: InferenceParams;
  language: string;
  existing_macros: MacroScenarioItem[];
}

/**
 * Defines the response from the /cover_hanging_requirements/ endpoint.
 */
export interface HangingRequirementCoverageResponse {
  new_macro_scenarios: MacroScenarioItem[];
  token_usage: TokenCount;
}

/**
 * Defines the request body for the /fix_requirement_coverage/ endpoint.
 */
export interface FixCoverageRequest {
  project: string;
  nome_sessione: string;
  story: string;
  uncovered_requirements_text: string;
  uncovered_requirement_ids: string[];
  existing_macros: MacroScenarioItem[];
  existing_micros: MicroScenarioV2Model[];
  language: string;
  inference_params?: InferenceParams;
}

/**
 * Defines the response from the /fix_requirement_coverage/ endpoint.
 */
export interface FixCoverageResponse {
  missing_requirement_ids: string[];
  new_macro_scenarios: MacroScenarioItem[];
  token_usage: TokenCount;
}

// Add new interfaces for the separate endpoints
export interface CoreAnalysisResult {
  core_analysis: {
    scenario_analysis: string;
    config_testdata_analysis: string;
  };
  ambiguities: Ambiguity[];
  token_usage: TokenCount;
}

export interface RequirementsAnalysisResult {
  requirements_analysis: {
    scenario_analysis: string;
    config_testdata_analysis: string;
  };
  token_usage: TokenCount;
}

// Interface for the response from POST /analyze/core
export interface CoreAnalysisResponse {
  core_analysis: AnalysisContent;
  raw_analysis_for_ambiguities: string;
  token_usage: TokenCount;
}

// --- NEW ---
// Interface for the response from POST /analyze/ambiguities
export interface AmbiguitiesResponse {
  ambiguities: any[]; // Use a stricter type like 'Ambiguity' if the backend shape matches
  token_usage: TokenCount;
}

// --- NEW ---
// This is the single state object your component will observe.
// It contains all the data and UI flags (loading/error).
export interface FullAnalysisResult {
  core_analysis: AnalysisContent | null;
  ambiguities: Ambiguity[];
  token_usage: TokenCount;
  isLoading: boolean; // Crucial for showing spinners
  error: string | null; // Crucial for showing error messages
}

/**
 * Represents a single, atomic constraint or business rule.
 * Matches the `Constraint` Pydantic model in the backend.
 */
export interface Constraint {
  id: string;
  description: string;
}

/**
 * Represents the response from the /extract/constraints endpoint.
 * Matches the `ConstraintsResponse` Pydantic model in the backend.
 */
export interface ConstraintsResponse {
  constraints: Constraint[]; // Assicurati che 'Constraint' sia importato o definito
  token_usage: TokenCount;
}

export interface StrategyAnalysisResponse {
  analysis_text: string;
  token_usage: TokenCount;
}

export interface DataParamModel {
  param_id?: string;
  field: string;
  valid_values: string[];
  invalid_values: string[];
  rules: string;
}

// --- NUOVE INTERFACCE DI SUPPORTO PER MICRO V2 (Per togliere gli 'any') ---

export interface MicroStep {
  step: string;
  covers_requirements: string[];
}

export interface MicroCheckpoint {
  checkpoint: string;
  expected_outcome: string; // "SUCCESS" | "BLOCKED" | "ALTERNATIVE"
  relates_to_requirements: string[];
}

export interface DataBinding {
  param_id: string;
  role: string;      // "driver" | "boundary" | "state" | "context"
  technique: string; // "EP" | "BVA"
  selection: string;
}

// --- MODELLO MICRO SCENARIO V2 AGGIORNATO ---

export interface MicroScenarioV2Model {
  micro_id: string;
  micro_key: string;
  macro_origin_id: string;
  title: string;
  intent: string;

  // Dati Logici (Aggiornati da 'any[]' a tipi concreti)
  entry_conditions?: string[];        // <--- Aggiunto (Opzionale per compatibilità)
  procedure_steps: MicroStep[];       // Prima era any[]
  checkpoints: MicroCheckpoint[];     // Prima era any[]

  // Motore di Generazione (NUOVO)
  data_binding: DataBinding[];        // <--- FONDAMENTALE PER TEST GEN V2

  requirements_covered: string[];
  parameters_used: string[];
  open_gaps?: string[];               // <--- Aggiunto (Opzionale)

  // Metadati UI
  unit_kind?: string;                 // <--- Aggiunto
  human_card?: string;                // <--- Aggiunto

  lifecycle_status: string;
  version: number;
}

export interface MacroSequenceItem {
  order: number;
  micro_id: string;
  title: string;
}

export interface MicroScenarioV2ListResult {
  micro_scenarios: MicroScenarioV2Model[];
  macro_sequence: MacroSequenceItem[]; // <--- ADD THIS LINE
  token_usage: TokenCount;
}

// Payload per GENERARE I MICRO (Fase 5)
export interface GenerateMicroV2RequestPayload {
  project: string;
  nome_sessione: string;
  macro_scenario: any;
  atomic_requirements_text: string;
  language: string;
  existing_micro_v2?: MicroScenarioV2Model[];
  data_parameters?: DataParamModel[];
  analysis_text?: string; // <--- NEW PARAM
}

// --- NUOVO: Payload per GENERARE I TEST (Fase 6) ---

export interface ExpandConfigModel {
  max_negative_per_param: number;
  dedup_mode: "NONE" | "CROSS_MICRO";
  renderer_mode: "PY_HTML" | "JSON_ONLY";
}

export interface GenerateTestsRequestPayload {
  project: string;
  language: string;
  micro_scenarios: MicroScenarioV2Model[];
  data_parameters: DataParamModel[];
  config: ExpandConfigModel;
  template_columns?: string[];
  macro_map?: Record<string, string>;
}
@Injectable({
  providedIn: 'root'
})
export class UserStoryService {
  private apiUrl = apiUrl;

  // Track ongoing requests to prevent duplicates
  private currentAnalysisRequest$: Observable<any> | null = null;
  private currentRequirementsRequest$: Observable<any> | null = null;
  private currentDataParamsRequest$: Observable<any> | null = null;

  // Create a cache for request parameters to detect identical requests
  private lastAnalysisParams: string | null = null;
  private lastRequirementsParams: string | null = null;

  // 1. Define the initial, empty state for our feature.
  private readonly initialState: FullAnalysisResult = {
    core_analysis: null,
    ambiguities: [],
    token_usage: { prompt_tokens: 0, completion_tokens: 0, total_tokens: 0 },
    isLoading: false,
    error: null,
  };

  // 2. Create the private BehaviorSubject to hold the state.
  private analysisResultSubject = new BehaviorSubject<FullAnalysisResult>(this.initialState);

  // 3. Expose a public observable. Components will subscribe to this.
  public analysisResult$ = this.analysisResultSubject.asObservable().pipe(
    distinctUntilChanged((prev, curr) =>
      prev.core_analysis === curr.core_analysis &&
      prev.isLoading === curr.isLoading &&
      prev.error === curr.error
    )
  );

  constructor(
    private http: HttpClient,
    private authService: AuthService,
    private statoSessioneService: StatoSessioneService
  ) { }

  private getHeaders(): Observable<HttpHeaders> {
    return this.authService.token$.pipe(
      switchMap(token => {
        const headers = new HttpHeaders().set('Authorization', `Bearer ${token}`);
        return new Observable<HttpHeaders>(observer => {
          observer.next(headers);
          observer.complete();
        });
      })
    );
  }

  /**
   * Generates requirements analysis from a story and its core analysis.
   * This version gets inference parameters directly from the StatoSessioneService.
   *
   * @param nome_sessione
   * @param story - The user story text, expected to be pre-processed with all context.
   * @param projectName
   * @param sessionName
   * @param coreAnalysis - The core analysis result (as a string) from generateCoreAnalysis.
   * @param language - The language for the response (e.g., 'en', 'it').
   * @returns Observable of RequirementsAnalysisResult containing structured requirements analysis.
   */
  generateRequirementsAnalysis(
    nome_sessione: string,
    story: string,
    projectName: string,
    coreAnalysis: string,
    language: string
  ): Observable<RequirementsAnalysisResult> {
    const sessionNameToUse = nome_sessione || `session-${new Date().toISOString()}`;
    return this.statoSessioneService.inferenceParams$.pipe(
      first(), // Takes the current value and completes
      switchMap(inferenceParams => {
        // meccanismo di caching molto semplice che previene l'invio di richieste identiche al server se una è già in corso
        const cacheKey = `${story}-${coreAnalysis}-${inferenceParams.model}-${language}`;

        if (this.currentRequirementsRequest$ && this.lastRequirementsParams === cacheKey) {
          console.log('[UserStoryService] Returning existing requirements analysis request');
          return this.currentRequirementsRequest$;
        }

        this.currentRequirementsRequest$ = null;
        this.lastRequirementsParams = cacheKey;

        this.currentRequirementsRequest$ = this.getHeaders().pipe(
          // We already have inferenceParams from the outer pipe, so we just use it
          switchMap((headers) => {
            // --- MODIFICA CHIAVE: Creazione del nuovo payload JSON ---
            const payload = {
              nome_sessione: sessionNameToUse,
              story: story,
              project: projectName,
              core_analysis: coreAnalysis,
              inference_params: inferenceParams, // Pass the entire params object
              language: language
            };

            console.log('[UserStoryService] Sending NEW requirements analysis request with payload:', payload);

            return this.http.post<any>(
              `${this.apiUrl}/getrequirements/`,
              payload,
              { headers: headers }
            );
          }),
          map((response: any) => {
            console.log('[UserStoryService] Requirements analysis response received');
            // This mapping logic remains the same as your original
            const resultObject: RequirementsAnalysisResult = {
              requirements_analysis: {
                scenario_analysis: response.requirements_analysis?.scenario_analysis || '',
                config_testdata_analysis: response.requirements_analysis?.config_testdata_analysis || ''
              },
              token_usage: {
                prompt_tokens: response.token_usage?.prompt_tokens || 0,
                completion_tokens: response.token_usage?.completion_tokens || 0,
                total_tokens: response.token_usage?.total_tokens || 0
              }
            };
            return resultObject;
          }),
          tap(result => {
            console.log('[UserStoryService] Requirements analysis token usage:', result.token_usage);
            if (result.token_usage) {
              this.statoSessioneService.updateTokenUsage(result.token_usage);
            }
          }),
          shareReplay({ refCount: true, bufferSize: 1 }),
          take(1),
          tap(() => {
            this.currentRequirementsRequest$ = null;
            this.lastRequirementsParams = null;
          }),
          catchError((error) => {
            this.currentRequirementsRequest$ = null;
            this.lastRequirementsParams = null;
            return this.handleError(error); // Assuming you have a handleError method
          })
        );

        return this.currentRequirementsRequest$;
      })
    );
  }

  /**
   * Generate configuration parameters and test data management analysis
   * @param projectId
   * @param sessionName
   * @param story - The user story text
   * @param language - The language for the response (e.g., 'en', 'it')
   * @param analysis - Optional core analysis text
   * @param supportiveAnalysis - Optional supportive analysis text
   * @returns Observable containing configuration parameters and test data management
   */
  generateDataParameters(
    projectId: string,
    sessionName: string,
    story: string,
    language: string,
    analysis?: string,
    supportiveAnalysis?: string
  ): Observable<DataParametersResponse> {
    // Create a cache key based on parameters
    const cacheKey = `${story}-${language}-${analysis || ''}-${supportiveAnalysis || ''}`;

    // Check if we have an identical request already in progress
    if (this.currentDataParamsRequest$) {
      console.log('[UserStoryService] Data parameters request already in progress');
      return this.currentDataParamsRequest$;
    }

    // Ottieni i parametri e le intestazioni per costruire il payload
    this.currentDataParamsRequest$ = this.getHeaders().pipe(
      withLatestFrom(this.statoSessioneService.inferenceParams$),
      switchMap(([headers, inferenceParams]) => {
        // Create a JSON payload
        const payload = {
          project: projectId,
          nome_sessione: sessionName,
          story,
          inference_params: inferenceParams,
          language,
          analysis,
          supportiveAnalysis
        };
        // Remove undefined or null properties
        Object.keys(payload).forEach(key => {
          if (payload[key] === undefined || payload[key] === null) {
            delete payload[key];
          }
        });

        console.log('[UserStoryService] Sending NEW data parameters request...');

        const authHeader = headers.get('Authorization');
        return this.http.post<DataParametersResponse>(
          `${this.apiUrl}/getdataparams/`,
          payload,
          {
            headers: new HttpHeaders({
              'Authorization': authHeader || '',
              'Content-Type': 'application/json'
            })
          }
        );
      }),
      map((response) => {
        // Handle the flexible response structure with presence flags
        return {
          analysis_results: {
            // Get the section presence flags or default to false
            has_config_parameters: response.analysis_results?.has_config_parameters ?? false,
            has_test_data_management: response.analysis_results?.has_test_data_management ?? false,
            has_base_dataset: response.analysis_results?.has_base_dataset ?? false,
            has_dynamic_data: response.analysis_results?.has_dynamic_data ?? false,
            has_sensitive_data: response.analysis_results?.has_sensitive_data ?? false,
            has_edge_cases: response.analysis_results?.has_edge_cases ?? false,

            // Get the actual data or use empty arrays
            configuration_parameters: response.analysis_results?.configuration_parameters || [],
            test_data_management: {
              base_dataset: response.analysis_results?.test_data_management?.base_dataset || [],
              dynamic_data: response.analysis_results?.test_data_management?.dynamic_data || [],
              sensitive_data: response.analysis_results?.test_data_management?.sensitive_data || [],
              edge_cases: response.analysis_results?.test_data_management?.edge_cases || []
            }
          },
          token_usage: {
            prompt_tokens: response.token_usage?.prompt_tokens || 0,
            completion_tokens: response.token_usage?.completion_tokens || 0,
            total_tokens: response.token_usage?.total_tokens || 0
          }
        };
      }),
      tap(result => {
        console.log('[UserStoryService] Data parameters response received');
        this.statoSessioneService.updateTokenUsage(result.token_usage);
      }),
      shareReplay({ refCount: true, bufferSize: 1 }), // PREVENT DUPLICATE CALLS
      take(1), // Ensure completion
      tap(() => {
        // Clear the current request when done
        this.currentDataParamsRequest$ = null;
      }),
      catchError((error) => {
        // Clear the current request on error too
        this.currentDataParamsRequest$ = null;
        return this.handleError(error);
      })
    );

    return this.currentDataParamsRequest$;
  }

  /**
   * Orchestrates the two-part analysis process: core analysis first, then ambiguities.
   * This function now gets inference parameters directly from the StatoSessioneService.
   */
  generateAnalysis(
    project: string,
    nome_sessione: string,
    story: string,
    language: string,
    lookForAmbiguities: boolean
  ): void {
    const sessionNameToUse = nome_sessione || `session-${new Date().toISOString()}`;
    // The cache key is simplified; a more robust key would stringify all params.
    const cacheKey = `${story}-${language}-${lookForAmbiguities}`;

    if (this.lastAnalysisParams === cacheKey && this.analysisResultSubject.getValue().isLoading) {
      console.log('[UserStoryService] Analysis already in progress with same parameters, ignoring duplicate call');
      return;
    }

    this.lastAnalysisParams = cacheKey;
    this.analysisResultSubject.next({ ...this.initialState, isLoading: true });

    const analysisRequest$ = this.getHeaders().pipe(
      // NUOVO: Usa `withLatestFrom` per ottenere i parametri di inferenza più recenti dal servizio di stato
      withLatestFrom(this.statoSessioneService.inferenceParams$),
      switchMap(([headers, inferenceParams]) => {

        // --- MODIFICA CHIAVE: Costruzione del nuovo payload JSON ---
        const payload = {
          project: project,
          nome_sessione: sessionNameToUse,
          story: story,
          // L'oggetto `inference_params` è ora annidato e completo
          inference_params: inferenceParams,
          language: language
        };

        console.log('Sending /analyze/core request with payload:', payload);

        // La prima chiamata HTTP ora invia il payload corretto
        return this.http.post<CoreAnalysisResponse>(`${this.apiUrl}/analyze/core`, payload, { headers })
          .pipe(
            switchMap(coreResponse => {
              // Notifica l'interfaccia con il risultato parziale (analisi di base completata)
              const partialResult: FullAnalysisResult = {
                ...this.initialState,
                core_analysis: coreResponse.core_analysis,
                token_usage: coreResponse.token_usage,
                // Mantieni isLoading=true se dobbiamo ancora cercare le ambiguità
                isLoading: lookForAmbiguities,
              };
              this.analysisResultSubject.next(partialResult);
              // Aggiorna lo stato centrale con i dati di base e l'uso dei token
              this.statoSessioneService.updateAnalysisData(coreResponse.core_analysis, null, null);
              this.statoSessioneService.updateTokenUsage(coreResponse.token_usage);

              if (!lookForAmbiguities) {
                // Se non dobbiamo cercare ambiguità, il processo finisce qui.
                return of({ ...coreResponse, ambiguities: [], final_token_usage: coreResponse.token_usage });
              }

              // Se dobbiamo continuare, prepariamo il payload per l'endpoint delle ambiguità.
              // Questo endpoint si aspetta ancora un payload flat basato sul tuo codice backend.
              const ambiguityPayload = {
                project: this.statoSessioneService.getProjectId(),
                nome_sessione: sessionNameToUse,
                story,
                inference_params: inferenceParams, // Passa l'intero oggetto
                language,
                raw_analysis: coreResponse.raw_analysis_for_ambiguities
              };

              console.log('Sending /analyze/ambiguities request with payload:', ambiguityPayload);

              return this.http.post<AmbiguitiesResponse>(`${this.apiUrl}/analyze/ambiguities`, ambiguityPayload, { headers })
                .pipe(
                  map(ambiguityResponse => ({
                    ...coreResponse,
                    ambiguities: ambiguityResponse.ambiguities,
                    final_token_usage: this.aggregateTokens(coreResponse.token_usage, ambiguityResponse.token_usage),
                  }))
                );
            }),
            tap(finalCombinedResponse => {
              const finalResult: FullAnalysisResult = {
                core_analysis: finalCombinedResponse.core_analysis,
                ambiguities: this._sanitizeAmbiguities(finalCombinedResponse.ambiguities),
                token_usage: finalCombinedResponse.final_token_usage,
                isLoading: false,
                error: null,
              };
              this.analysisResultSubject.next(finalResult);

              console.log('[UserStoryService] Analysis complete. Updating central session state.');
              // Aggiorna lo stato centrale con tutti i dati finali
              this.statoSessioneService.updateAnalysisData(
                finalResult.core_analysis,
                null, // requirements not generated yet
                null, // supportive not generated yet
                finalResult.ambiguities
              );
              this.statoSessioneService.updateTokenUsage(finalResult.token_usage);
            }),
            catchError(error => {
              const errorMessage = error.error?.detail || error.message || 'An unknown error occurred during analysis.';
              console.error('[UserStoryService] Analysis pipeline error:', error);
              const currentResult = this.analysisResultSubject.getValue();
              this.analysisResultSubject.next({
                ...currentResult,
                isLoading: false,
                error: errorMessage,
              });
              return of(null); // Return a non-error observable to complete the stream
            })
          );
      }),
      shareReplay({ refCount: true, bufferSize: 1 }),
      take(1),
      tap(() => {
        // Resetta il cache key dopo che la chiamata è completata (o fallita)
        this.lastAnalysisParams = null;
      }),
      catchError((error) => {
        // Questo catchError gestisce errori prima della chiamata HTTP (es. getHeaders fallisce)
        this.lastAnalysisParams = null;
        return throwError(error);
      })
    );

    analysisRequest$.subscribe();
  }

  private aggregateTokens(tokens1: TokenCount, tokens2: TokenCount): TokenCount {
    return {
      prompt_tokens: tokens1.prompt_tokens + tokens2.prompt_tokens,
      completion_tokens: tokens1.completion_tokens + tokens2.completion_tokens,
      total_tokens: tokens1.total_tokens + tokens2.total_tokens,
    };
  }

  private _sanitizeAmbiguities(ambiguities: any[]): Ambiguity[] {
    return (ambiguities || []).map((ambig: any) => {
      const backendStato = ambig.stato;
      const backendType = ambig.type;
      const finalStato = backendStato && ['unanswered', 'answered', 'solved'].includes(backendStato) ? backendStato : 'unanswered';
      const finalType = backendType && ['technical', 'workflow'].includes(backendType) ? backendType : 'technical';
      return { ...ambig, stato: finalStato, type: finalType } as Ambiguity;
    });
  }

  /**
   * REVISED: Processes resolved ambiguities to update the CORE ANALYSIS and create KB items.
   * This is now the FIRST step in a multi-step process orchestrated by the component.
   * @returns An Observable of ProcessAmbiguitiesResponse, containing the new core_analysis and KB data.
   */
  processAmbiguities(
    nome_sessione: string,
    story: string,
    analysisText: string,
    language: string,
    projectName: string,
    userName: string,
    ambiguities: Ambiguity[]
  ): Observable<ProcessAmbiguitiesResponse> {

    // Get headers and the latest inference params from the state service
    return this.getHeaders().pipe(
      withLatestFrom(this.statoSessioneService.inferenceParams$),
      switchMap(([headers, inferenceParams]) => {

        // Construct the new payload with the nested `inference_params` object
        const payload = {
          nome_sessione: nome_sessione,
          story: story,
          analysis: analysisText,
          inference_params: inferenceParams,
          language: language,
          projectName: projectName,
          userName: userName,
          ambiguities: ambiguities
        };

        const url = `${this.apiUrl}/processambiguities/`;

        console.log(`[UserStoryService] Sending ${ambiguities.length} ambiguities to regenerate core analysis with payload:`, payload);

        // Make the HTTP POST call with the new payload and correct response type
        return this.http.post<ProcessAmbiguitiesResponse>(url, payload, { headers });
      }),
      tap(response => {
        console.log('[UserStoryService] Core analysis regenerated successfully by the backend.');
        if (response.token_usage) {
          this.statoSessioneService.updateTokenUsage(response.token_usage);
        }
      }),
      catchError(this.handleError) // Assuming you have a handleError method
    );
  }

  /**
   * Retrieve all knowledge base items for a specific user and project
   * @param userName - The username of the owner
   * @param projectName - The project name the knowledge base items belong to
   * @returns Observable of an array of knowledge base items
   */
  getKnowledgeBaseItems(
    userName: string,
    projectName: string
  ): Observable<KnowledgeBaseItem[]> {
    return this.getHeaders().pipe(
      switchMap((headers) => {
        /*console.log('Fetching knowledge base items for:', {
          userName,
          projectName
        });*/

        const params = {
          userName,
          projectName
        };

        const authHeader = headers.get('Authorization');
        return this.http.get<KnowledgeBaseItem[]>(
          `${this.apiUrl}/knowledgebase/`,
          {
            headers: new HttpHeaders({
              'Authorization': authHeader || ''
            }),
            params
          }
        );
      }),
      tap(result => {
        // console.log('Knowledge base items retrieved:', result.length);
      }),
      catchError(this.handleError)
    );
  }

  /**
   * Finds potential Knowledge Base item matches for a list of ambiguities.
   * @param ambiguities - An array of Ambiguity objects (expected without linkedKbItems).
   * @param userName - The username for KB lookup.
   * @param projectName - The project name for KB lookup.
   * @param limit_per_ambiguity - Maximum number of similar KB items to retrieve per ambiguity. Default is 10.
   * @returns Observable of FindKbMatchesResponse containing updated ambiguities and token usage.
   */
  findKbMatches(
    ambiguities: Ambiguity[],
    userName: string,
    projectName: string,
    nome_sessione: string,
    limit_per_ambiguity?: number, // Made optional with ? operator and added default in implementation
  ): Observable<FindKbMatchesResponse> {
    return this.getHeaders().pipe(
      switchMap((headers) => {
        const url = `${this.apiUrl}/findkbmatches/`; // Matches your FastAPI endpoint path
        const requestBody: any = {
          ambiguities: ambiguities,
          userName: userName,
          project: projectName,
          nome_sessione: nome_sessione
        };

        // Only add limit_per_ambiguity to params if it's provided
        if (limit_per_ambiguity !== undefined) {
          requestBody.limit_per_ambiguity = limit_per_ambiguity;
        }

        /*console.log('Sending find KB matches request:', {
          url: url,
          userName: userName,
          projectName: projectName,
          ambiguityCount: ambiguities.length,
          limit_per_ambiguity: limit_per_ambiguity ?? 'default (10)' // Log with default note if not provided
        });*/

        return this.http.post<FindKbMatchesResponse>(
          url,
          requestBody,
          {
            headers: headers
          }
        );
      }),
      tap((response: FindKbMatchesResponse) => {
        // Extract data from the response object
        const updatedAmbiguities = response.ambiguities;
        const tokenUsage = response.token_usage;

        // Log ambiguity processing results
        const linkedCount = updatedAmbiguities.filter(a => a.linkedKbItems && a.linkedKbItems.length > 0).length;
        // console.log(`Find KB matches response received. ${updatedAmbiguities.length} ambiguities processed, ${linkedCount} ambiguities now have potential KB links.`);

        // Log and process token usage (consistent with other methods)
        // console.log('Token usage for findKbMatches:', tokenUsage);
        if (this.statoSessioneService && tokenUsage) {
          this.statoSessioneService.updateTokenUsage(tokenUsage);
          // console.log('Session token usage updated via findKbMatches.');
        } else {
          console.warn('StatoSessioneService not available or tokenUsage missing, cannot update session tokens.');
        }
      }),
      catchError(this.handleError)
    );
  }

  /**
   * Create a new knowledge base item
   * @param userName - The username of the owner
   * @param projectName - The project name the item belongs to
   * @param item - The knowledge base item data (partial, without IDs/timestamps)
   * @returns Observable of the created knowledge base item
   */
  createKnowledgeBaseItem(
    userName: string,
    projectName: string,
    item: Partial<KnowledgeBaseItem>
  ): Observable<KnowledgeBaseItem> {
    return this.getHeaders().pipe(
      switchMap((headers) => {
        /*console.log('Creating knowledge base item:', {
          userName,
          projectName,
          item: { ...item, term: item.term }
        });*/

        // Ensure all required fields are present
        const payload = {
          ...item,
          userName,
          projectName,
          // The backend will generate the kb_id using the format:
          // item_type-projectName-batch_uuid-timestamp-index
          // The backend handles kb_id, createdAt and lastModified fields
          title: item.title || item.term, // Use the term as title if not provided
        };

        return this.http.post<KnowledgeBaseItem>(
          `${this.apiUrl}/knowledgebase/`,
          payload,
          { headers }
        );
      }),
      tap(result => {
        // console.log('Knowledge base item created:', result);
      }),
      catchError(this.handleError)
    );
  }

  /**
   * Update an existing knowledge base item
   * @param userName - The username of the owner
   * @param projectName - The project name the item belongs to
   * @param itemId - The MongoDB _id of the item to update
   * @param item - The updated knowledge base item data
   * @returns Observable of the updated knowledge base item
   */
  updateKnowledgeBaseItem(
    userName: string,
    projectName: string,
    itemId: string,
    item: Partial<KnowledgeBaseItem>
  ): Observable<KnowledgeBaseItem> {
    return this.getHeaders().pipe(
      switchMap((headers) => {
        /*console.log('Updating knowledge base item:', {
          userName,
          projectName,
          itemId,
          item: { ...item, term: item.term }
        });*/

        // Only include updated fields and metadata
        // The backend will handle the lastModified field
        const payload = {
          ...item,
          userName,
          projectName
        };

        return this.http.put<KnowledgeBaseItem>(
          `${this.apiUrl}/knowledgebase/${itemId}`,
          payload,
          { headers }
        );
      }),
      tap(result => {
        // console.log('Knowledge base item updated:', result);
      }),
      catchError(this.handleError)
    );
  }

  /**
   * Delete a knowledge base item
   * @param userName - The username of the owner
   * @param projectName - The project name the item belongs to
   * @param itemId - The MongoDB _id of the item to delete
   * @returns Observable of the response message
   */
  deleteKnowledgeBaseItem(
    userName: string,
    projectName: string,
    itemId: string
  ): Observable<{ message: string }> {
    return this.getHeaders().pipe(
      switchMap((headers) => {
        /*console.log('Deleting knowledge base item:', {
          userName,
          projectName,
          itemId
        });*/

        const params = new HttpParams()
          .set('userName', userName)
          .set('projectName', projectName);

        return this.http.delete<{ message: string }>(
          `${this.apiUrl}/knowledgebase/${itemId}`,
          {
            headers,
            params
          }
        );
      }),
      tap(result => {
        // console.log('Knowledge base item deleted:', result);
      }),
      catchError(this.handleError)
    );
  }

  generateStrategyAnalysis(
    project: string,
    nome_sessione: string,
    story: string,
    atomic_requirements: string,
    language: string
  ): Observable<StrategyAnalysisResponse> {
    return this.getHeaders().pipe(
      withLatestFrom(this.statoSessioneService.inferenceParams$),
      switchMap(([headers, inferenceParams]) => {
        const payload = {
          project,
          nome_sessione,
          story,
          atomic_requirements_text: atomic_requirements,
          inference_params: inferenceParams,
          language
        };
        return this.http.post<StrategyAnalysisResponse>(
          `${this.apiUrl}/analyze_requirements_text/`,
          payload,
          { headers }
        );
      }),
      tap(result => {
        if (result.token_usage) {
          this.statoSessioneService.updateTokenUsage(result.token_usage);
        }
      })
    );
  }

  /**
   * Generates Macro Scenarios based *only* on analysis text and user story.
   * Calls the simplified backend endpoint `/generatemacroscenarios/`.
   * @param projectId
   * @param sessionName
   * @param analisi - analisi della storia
   * @param requisiti - requisiti individuati nella storia
   * @param language - The target language
   * @returns Observable of MacroScenarioListResult containing MacroScenarioItem[] with structured steps.
   */
  generateMacroScenarios(
    projectId: string,
    sessionName: string,
    analisi: string,
    requisiti: string,
    language: string,
    strategy_analysis_text?: string // <--- NEW PARAM
  ): Observable<MacroScenarioListResult> {
    if (!requisiti || requisiti.trim() === '') {
      return throwError(new Error('Analysis text cannot be empty for scenario generation.'));
    }

    return this.getHeaders().pipe(
      // Ottieni i parametri di inferenza più recenti dal servizio di stato
      withLatestFrom(this.statoSessioneService.inferenceParams$),
      switchMap(([headers, inferenceParams]) => {
        // Costruisci il nuovo payload standardizzato
        const requestData = {
          projectId: projectId,
          sessionName: sessionName,
          story: analisi,
          analysis_text: requisiti,
          inference_params: inferenceParams, // Passa l'intero oggetto
          language: language,
          strategy_context: strategy_analysis_text || null // <--- PASS NEW CO-T TEXT HERE
        };

        console.log('[UserStoryService] Sending macro scenario generation request with payload:', requestData);

        return this.http.post<MacroScenarioListResult>(
          `${this.apiUrl}/generatemacroscenarios/`,
          requestData,
          { headers: headers }
        );
      }),
      map((response: MacroScenarioListResult): MacroScenarioListResult => {
        // logica di mapping
        return {
          scenarios: response.scenarios || [],
          analysis_text: response.analysis_text || '',
          token_usage: {
            prompt_tokens: response.token_usage?.prompt_tokens || 0,
            completion_tokens: response.token_usage?.completion_tokens || 0,
            total_tokens: response.token_usage?.total_tokens || 0
          }
        };
      }),
      tap(result => {
        console.log(`[UserStoryService] Macro scenario generation returned ${result.scenarios.length} scenario(s).`);
      }),
      shareReplay({ refCount: true, bufferSize: 1 }),
      take(1),
      catchError(this.handleError)
    );
  }

  /**
   * Generates Micro Scenario Specifications for a given Macro Scenario based on a list of Atomic Requirements.
   * This single, powerful method replaces the old two-step process.
   *
   * @param progetto
   * @param sessione
   * @param macroScenario The Macro Scenario object being analyzed.
   * @param atomicRequirementsText The complete list of atomic requirements (your `coreAnalysis.scenario_analysis`).
   * @param language The target language for the output.
   * @param existingMicroSpecsContext Optional array of existing micro-specifications for reuse analysis.
   * @param availableTestDataElementsString Added new parameter
   * @returns An Observable of the list of generated micro-specifications.
   */
  generateMicroSpecsFromRequirements(
    progetto: string,
    sessione: string,
    macroScenario: MacroScenarioItem,
    atomicRequirementsText: string,
    language: string,
    existingMicroSpecsContext: MicroScenarioSpecificationModel[],
    availableTestDataElementsString: string
  ): Observable<MicroScenarioSpecificationListResultModel> {

    // Ottieni i parametri di inferenza e le intestazioni prima di fare la chiamata
    return this.getHeaders().pipe(
      // Usa withLatestFrom per ottenere i parametri più recenti dal servizio di stato
      withLatestFrom(this.statoSessioneService.inferenceParams$),
      switchMap(([headers, inferenceParams]) => {
        // Interfaccia per il corpo della richiesta, ora include inference_params
        interface MicroSpecFromReqsBody {
          project: string,
          nome_sessione: string,
          macro_scenario: MacroScenarioItem;
          atomic_requirements_text: string;
          inference_params: InferenceParams;
          language: string;
          existing_micro_specifications: MicroScenarioSpecificationModel[];
          available_test_data_elements_string: string;
        }

        // Costruisci il nuovo payload
        const payload: MicroSpecFromReqsBody = {
          project: progetto,
          nome_sessione: sessione,
          macro_scenario: macroScenario,
          atomic_requirements_text: atomicRequirementsText,
          inference_params: inferenceParams, // <-- MODIFICATO: passa l'intero oggetto
          language: language,
          existing_micro_specifications: existingMicroSpecsContext,
          available_test_data_elements_string: availableTestDataElementsString
        };

        const endpoint = `${this.apiUrl}/generate_all_micro_specifications_for_macro/`;

        console.log(`[UserStoryService] Sending micro-specs generation request for Macro ID: ${macroScenario.id}`, payload);

        return this.http.post<MicroScenarioSpecificationListResultModel>(endpoint, payload, { headers });
      }),
      map((response): MicroScenarioSpecificationListResultModel => {
        const specs = response?.specifications?.map(spec => ({
          ...spec,
          title: spec.title || '',
          tags: spec.tags || [],
          test_data_elements: spec.test_data_elements || [],
          covered_requirements: spec.covered_requirements || []
        })) || [];

        const tokens = response?.token_usage || { prompt_tokens: 0, completion_tokens: 0, total_tokens: 0 };

        return {
          specifications: specs,
          token_usage: tokens
        };
      }),
      tap(result => {
        console.log(`[UserStoryService] Micro-spec generation for Macro ID ${macroScenario.id} returned ${result.specifications.length} spec(s).`);
      }),
      shareReplay({ refCount: true, bufferSize: 1 }), // PREVENT DUPLICATE CALLS
      take(1), // Ensure completion
      catchError(this.handleError)
    );
  }
  /**
   * [V2] Generates Micro Scenarios using the new V2 Logic.
   * Gestisce automaticamente l'aggiunta di inference_params.
   */
  generateMicroScenariosV2(requestData: GenerateMicroV2RequestPayload): Observable<MicroScenarioV2ListResult> {
    return this.getHeaders().pipe(
      // 1. Recuperiamo i parametri AI aggiornati dallo stato
      withLatestFrom(this.statoSessioneService.inferenceParams$),
      switchMap(([headers, inferenceParams]) => {

        // 2. Costruiamo il payload completo per il backend
        const fullPayload = {
          ...requestData,
          inference_params: inferenceParams // Aggiungiamo il pezzo mancante
        };

        const url = `${this.apiUrl}/generate_micro_scenarios_v2_for_macro/`;
        console.log('[UserStoryService] Sending V2 Micro generation request:', fullPayload);

        // 3. Eseguiamo la chiamata
        return this.http.post<MicroScenarioV2ListResult>(url, fullPayload, { headers });
      }),
      tap(result => {
        console.log(`[UserStoryService] V2 Generation successful. Received ${result.micro_scenarios.length} scenarios.`);
        if (result.token_usage) {
          this.statoSessioneService.updateTokenUsage(result.token_usage);
        }
      }),
      catchError(this.handleError)
    );
  }

  refineTestSuite(
    project: string,
    nome_sessione: string,
    user_story: string,
    raw_test_cases: RawTestCase[],
    template_description: string,
    output_template: any[],
    language: string
  ): Observable<RefinementResponse> {

    return this.getHeaders().pipe(
      // Ottieni gli ultimi parametri di inferenza
      withLatestFrom(this.statoSessioneService.inferenceParams$),
      switchMap(([headers, inferenceParams]) => {

        // Costruisci il nuovo payload
        const requestPayload: RefinementRequest = {
          project,
          nome_sessione,
          user_story,
          raw_test_cases,
          template_description,
          output_template,
          inference_params: inferenceParams, // <-- Modifica chiave
          language
        };

        const url = `${this.apiUrl}/refine_test_suite/`;
        console.log('Sending test suite refinement request to:', url, 'with payload:', requestPayload);

        return this.http.post<RefinementResponse>(url, requestPayload, { headers });
      }),
      tap(result => {
        console.log(`Refinement successful. Received ${result.tests.length} refined test cases.`);
        if (result.token_usage) {
          this.statoSessioneService.updateTokenUsage(result.token_usage);
        }
      }),
      catchError(this.handleError)
    );
  }

  groupTestsForConsolidation(
    project: string,
    nome_sessione: string,
    user_story: string,
    raw_test_cases: RawTestCase[],
    language: string
  ): Observable<GroupingResponse> {
    // La firma ora non ha più bisogno di 'model'

    return this.getHeaders().pipe(
      // Ottieni gli ultimi parametri di inferenza dal servizio di stato
      withLatestFrom(this.statoSessioneService.inferenceParams$),
      switchMap(([headers, inferenceParams]) => {

        // Costruisci il nuovo payload con l'oggetto inference_params completo
        const requestPayload: GroupingRequest = {
          project,
          nome_sessione,
          user_story,
          raw_test_cases,
          inference_params: inferenceParams,
          language
        };

        const url = `${this.apiUrl}/group_tests_for_consolidation/`;
        console.log('Sending group for consolidation request to:', url, 'with payload:', requestPayload);

        return this.http.post<GroupingResponse>(url, requestPayload, { headers }).pipe(
          tap(result => {
            console.log(`Grouping successful. Received ${result.consolidation_groups.length} groups.`);
            if (result.token_usage) {
              this.statoSessioneService.updateTokenUsage(result.token_usage as any);
            }
          }),
          // Puoi mantenere un catchError specifico se necessario
          catchError(this.handleError)
        );
      })
    );
  }

  /**
   * Extracts a structured list of operational constraints from a refined scenario analysis.
   * Calls the backend endpoint `/extract/constraints`.
   * @param progetto
   * @param nome_sessione
   * @param analysisText The complete and refined scenario analysis text.
   * @param language The target language for the response.
   * @returns An Observable of ConstraintsResponse containing the list of constraints and token usage.
   */
  extractConstraints(
    progetto: string,
    nome_sessione: string,
    analysisText: string,
    language: string
  ): Observable<ConstraintsResponse> {
    const sessionNameToUse = nome_sessione || `session-${new Date().toISOString()}`;
    // Il metodo ora ottiene i parametri di inferenza dal servizio di stato
    return this.getHeaders().pipe(
      // Ottieni i parametri più recenti
      withLatestFrom(this.statoSessioneService.inferenceParams$),
      switchMap(([headers, inferenceParams]) => {
        const url = `${this.apiUrl}/extract/constraints`;

        // Crea il nuovo payload con l'oggetto `inference_params`
        const payload = {
          progetto: progetto,
          nome_sessione: sessionNameToUse,
          analysis: analysisText,
          inference_params: inferenceParams,
          language: language
        };

        console.log(`[UserStoryService] Sending constraints extraction request with new payload...`, payload);

        return this.http.post<ConstraintsResponse>(url, payload, { headers });
      }),
      tap(response => {
        console.log(`[UserStoryService] Constraints extraction successful. Received ${response.constraints.length} constraints.`);
        if (response.token_usage) {
          this.statoSessioneService.updateTokenUsage(response.token_usage);
          console.log('[UserStoryService] Session token usage updated from constraints extraction.');
        }
      }),
      catchError(this.handleError) // Assumendo che tu abbia un metodo handleError
    );
  }

  /**
   * Calls the backend to generate one or more new Macro Scenarios to cover
   * requirements that were previously missed.
   */
  coverHangingRequirement(
    // Diciamo a TypeScript: "Mi aspetto tutti i campi di HangingRequirementCoverageRequest
    // TRANNE 'inference_params', perché lo aggiungerò io."
    requestData: Omit<HangingRequirementCoverageRequest, 'inference_params'>
  ): Observable<HangingRequirementCoverageResponse> {

    return this.getHeaders().pipe(
      withLatestFrom(this.statoSessioneService.inferenceParams$),
      switchMap(([headers, inferenceParams]) => {
        const url = `${this.apiUrl}/cover_hanging_requirements/`;

        // Costruiamo il payload finale che SODDISFA l'interfaccia completa
        const finalPayload: HangingRequirementCoverageRequest = {
          ...requestData,
          inference_params: inferenceParams // Aggiungiamo il campo mancante
        };

        console.log('[UserStoryService] Sending request to cover hanging requirements with payload:', finalPayload);
        return this.http.post<HangingRequirementCoverageResponse>(url, finalPayload, { headers });
      }),
      tap(response => {
        console.log(`[UserStoryService] Successfully received coverage: ${response.new_macro_scenarios.length} new macro(s).`);
        if (response.token_usage) {
          this.statoSessioneService.updateTokenUsage(response.token_usage);
        }
      }),
      catchError(this.handleError)
    );
  }

  /**
   * [V2] Calls the backend to bridge Macro and Micro scenarios, 
   * identifying gaps and generating specific strategic fixes.
   */
  fixRequirementCoverage(
    requestData: Omit<FixCoverageRequest, 'inference_params'>
  ): Observable<FixCoverageResponse> {
    return this.getHeaders().pipe(
      withLatestFrom(this.statoSessioneService.inferenceParams$),
      switchMap(([headers, inferenceParams]) => {
        const url = `${this.apiUrl}/fix_requirement_coverage/`;
        const finalPayload: FixCoverageRequest = {
          ...requestData,
          inference_params: inferenceParams
        };
        console.log('[UserStoryService] Sending request to FIX coverage with payload:', finalPayload);
        return this.http.post<FixCoverageResponse>(url, finalPayload, { headers });
      }),
      tap(response => {
        console.log(`[UserStoryService] Fix coverage successful. Found ${response.missing_requirement_ids.length} missing IDs. Created ${response.new_macro_scenarios.length} new macros.`);
        if (response.token_usage) {
          this.statoSessioneService.updateTokenUsage(response.token_usage);
        }
      }),
      catchError(this.handleError)
    );
  }

  answerChat(
    project: string,
    sessionName: string,
    context: string,
    question: string,
    language: string,
    modelOverride?: string
  ): Observable<ChatResponse> {
    return this.getHeaders().pipe(
      withLatestFrom(this.statoSessioneService.inferenceParams$),
      switchMap(([headers, inferenceParams]) => {
        const params = modelOverride
          ? { ...inferenceParams, model: modelOverride }
          : inferenceParams;
        const payload = {
          project: project,
          nome_sessione: sessionName,
          context: context,
          question: question,
          inference_params: params,
          language: language
        };
        const url = `${this.apiUrl}/chat/answer/`;
        return this.http.post<ChatResponse>(url, payload, { headers });
      }),
      tap(result => {
        if (result.token_usage) {
          this.statoSessioneService.updateTokenUsage(result.token_usage);
        }
      }),
      catchError(this.handleError)
    );
  }

  agentChat(
    project: string,
    question: string,
    modelOverride?: string,
    maxIterations?: number,
    history?: { role: string; content: string }[]
  ): Observable<AgentChatResponse> {
    return this.getHeaders().pipe(
      withLatestFrom(this.statoSessioneService.inferenceParams$),
      switchMap(([headers, inferenceParams]) => {
        const model = modelOverride || inferenceParams.model;
        const payload: any = {
          project: project,
          question: question,
          model: model
        };
        if (maxIterations) {
          payload.max_iterations = maxIterations;
        }
        if (history?.length) {
          payload.history = history;
        }
        const url = `${this.apiUrl}/agent-chat/`;
        return this.http.post<AgentChatResponse>(url, payload, { headers });
      }),
      tap(result => {
        if (result.token_usage) {
          this.statoSessioneService.updateTokenUsage(result.token_usage);
        }
      }),
      catchError(this.handleError)
    );
  }

  /**
   * Run the KB search agent for each ambiguity and return proposed resolutions
   * with full debug trace.
   */
  agentKbResolve(
    project: string,
    ambiguities: Ambiguity[],
    story: string = '',
    modelOverride?: string
  ): Observable<AgentKbResolveResponse> {
    return this.getHeaders().pipe(
      withLatestFrom(this.statoSessioneService.inferenceParams$),
      switchMap(([headers, inferenceParams]) => {
        const model = modelOverride || inferenceParams.model;
        const payload = {
          project,
          ambiguities,
          model,
          story,
          language: this.statoSessioneService.getSelectedLanguage || 'italiano',
        };
        const url = `${this.apiUrl}/agent-kb-resolve/`;
        return this.http.post<AgentKbResolveResponse>(url, payload, { headers });
      }),
      tap(result => {
        if (result.token_usage) {
          this.statoSessioneService.updateTokenUsage(result.token_usage);
        }
      }),
      catchError(this.handleError)
    );
  }

  /**
   * Resolve a single ambiguity via the KB search agent.
   * Designed to be called in parallel (one per ambiguity).
   */
  agentKbResolveSingle(
    project: string,
    ambiguity: Ambiguity,
    story: string = '',
    modelOverride?: string
  ): Observable<AgentKbResolveSingleResponse> {
    return this.getHeaders().pipe(
      withLatestFrom(this.statoSessioneService.inferenceParams$),
      switchMap(([headers, inferenceParams]) => {
        const model = modelOverride || inferenceParams.model;
        const payload = {
          project,
          ambiguity,
          model,
          story,
          language: this.statoSessioneService.getSelectedLanguage || 'italiano',
        };
        const url = `${this.apiUrl}/agent-kb-resolve-single/`;
        return this.http.post<AgentKbResolveSingleResponse>(url, payload, { headers });
      }),
      tap(result => {
        if (result.token_usage) {
          this.statoSessioneService.updateTokenUsage(result.token_usage);
        }
      }),
      catchError(this.handleError)
    );
  }

  /**
   * Handle HTTP errors
   * @param error - The HTTP error response
   * @returns An observable with a user-facing error message
   */
  private handleError(error: HttpErrorResponse) {
    let errorMessage = 'An error occurred. Please try again later.';

    if (error.error instanceof ErrorEvent) {
      // Client-side error
      errorMessage = `Error: ${error.error.message}`;
    } else {
      // Server-side error
      console.error('Backend error object:', error.error); // Add this line to log the full error object

      switch (error.status) {
        case 400:
          errorMessage = error.error.detail || 'Invalid request. Please check your input.';
          break;
        case 401:
          errorMessage = 'Unauthorized. Please log in again.';
          break;
        case 403:
          errorMessage = 'Access denied. You don\'t have permission to perform this action.';
          break;
        case 422: // Add specific handling for 422 to get detailed validation errors
          errorMessage = error.error.detail ? JSON.stringify(error.error.detail) : 'Validation error. Please check the request payload.';
          break;
        case 500:
          errorMessage = 'Server error. Please try again later.';
          break;
        default: // Handle other unexpected statuses
          errorMessage = `Server returned code ${error.status}: ${error.statusText}`;
          if (error.error && typeof error.error === 'object' && error.error.detail) {
            errorMessage += ` - Detail: ${JSON.stringify(error.error.detail)}`;
          }
          break;
      }
    }

    console.error('Error in template-api service:', error);
    return throwError(new Error(errorMessage));
  }
}
